import {Injectable} from '@angular/core';
import {PhoneNumber} from './phone-number.service';
import {Server, ServerService} from './server.service';
import {Http, Response} from '@angular/http';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {UserService} from './user.service';
import {Observable} from 'rxjs/Observable';
import {UUID} from 'angular2-uuid';
import {isNullOrUndefined} from 'util';
import {Subscription} from 'rxjs/Subscription';

@Injectable()
export class DialerService {

  static readonly dialerResourceURL = '/api/dialer/id/';

  static readonly getLastCallURL = '/api/dialer/lastCalls/1';

  static readonly addNewCallURL = '/api/dialer';


  actualCallSubject: BehaviorSubject<DialerCall> = new BehaviorSubject<DialerCall>(null);

  actualCallSubscription: Subscription = null;

  static needUnSubscribe(dc: DialerCall, acs: Subscription): boolean {
    if (isNullOrUndefined(acs) || acs.closed) {
      return false;
    }
    return dc.isFinished();
  }

  constructor(private http: Http,
              private us: UserService, ss: ServerService) {
    this.updateLastCall();
  }

  updateLastCall(): void {
    this.http.get(DialerService.getLastCallURL, {headers: this.us.getAuthHeaders()})
      .map((res: Response) => DialerCall.fromJSON(res.json().data))
      .catch((error: any) => {
        console.log(`${JSON.stringify(error)}`);
        if (error.status === 404) {
          const firstPhone = this.us.currentUser.getValue().getPhones()[0];
          const username = this.us.currentUser.getValue().username;
          const customerId = this.us.currentUser.getValue().customerId;
          return Observable.of(new DialerCall('0', firstPhone, new PhoneNumber('55', '31', '3217', '9200'), 0, username, customerId,
            'context', DialerCallState.FINISHED, null, '0', new Channel('0', ChannelState.BUSY),
            new Channel('0', ChannelState.ANSWERED), 0));
        } else {
          return Observable.throw(error.json().message || 'Server error');
        }
      })
      .subscribe((dc: DialerCall) => {
        this.actualCallSubject.next(dc);
        console.log(`Got last call from server from ${dc.srcNumber.getE164Format()}` +
          `(${DialerCallState[dc.srcChannel.channelState]}) to ` +
          `${dc.dstNumber.getE164Format()}(${DialerCallState[dc.srcChannel.channelState]})` +
          ` - ${DialerCallState[dc.state]}`);
        this.subscribeCallStatus(dc.id);
      });
  }

  createCall(src: PhoneNumber, dst: PhoneNumber): void {

    const body: any = new Object();
    body['srcNumber'] = src;
    body['dstNumber'] = dst;
    body['id'] = UUID.UUID();
    this.http.post(DialerService.addNewCallURL, body, {headers: this.us.getAuthHeaders()})
      .map((res: Response) => DialerCall.fromJSON(res.json().data))
      .catch((error: any) => Observable.throw(error.json().message || 'Server error'))
      .subscribe((dc: DialerCall) => {
        this.actualCallSubject.next(dc);
        console.log(`Sent an call to server and received this response: ${JSON.stringify(dc)}`);
        this.subscribeCallStatus(dc.id);
      });
  }

  getCall(id: String): Observable<DialerCall> {
    return this.http.get(DialerService.dialerResourceURL + id, {headers: this.us.getAuthHeaders()})
      .map((res: Response) => DialerCall.fromJSON(res.json().data))
      .catch((error: any) => Observable.throw(error.json().message || 'Server error'));
  }

  hangupCall(id: String): Observable<String> {
    return this.http.delete(DialerService.dialerResourceURL + id, {headers: this.us.getAuthHeaders()})
      .map((res: Response) => {
        if (res.status === 200) {
          return res.json().message;
        } else {
          console.log(`Error trying to hangup call ${res.status}-${res.text()}`);
          return 'ERROR';
        }
      })
      .catch((error: any) => Observable.throw(error.json().message || 'Server error'));
  }

  subscribeCallStatus(id: String) {
    if (!isNullOrUndefined(this.actualCallSubscription)) {
      this.actualCallSubscription.unsubscribe();
    }
    this.actualCallSubscription = Observable
      .interval(3000)
      .retry(3)
      .flatMap((i: number) => {
        return this.getCall(id);
      })
      .subscribe((dc: DialerCall) => {
        if (DialerService.needUnSubscribe(dc, this.actualCallSubscription)) {
          this.actualCallSubscription.unsubscribe();
        }
        this.actualCallSubject.next(dc);
        console.log(`DialerCall: ${JSON.stringify(dc)}`);
      });
  }
}


export class DialerCall {

  static fromJSON(json: any): DialerCall {
    const object: DialerCall = Object.create(DialerCall.prototype);
    Object.assign(object, json);
    object.state = DialerCallState[<string>json.state];
    object.server = Server.fromJSON(json.server);
    object.srcNumber = PhoneNumber.fromJSON(json.srcNumber);
    object.dstNumber = PhoneNumber.fromJSON(json.dstNumber);
    object.srcChannel = Channel.fromJSON(json.srcChannel);
    object.dstChannel = Channel.fromJSON(json.dstChannel);
    return object;
  }

  constructor(public id: string, public srcNumber: PhoneNumber, public dstNumber: PhoneNumber, public createdTime: number,
              public username: string, public customerId: string, public context: string, public state: DialerCallState,
              public server: Server, public serverCallId: string, public srcChannel: Channel, public dstChannel: Channel,
              public duration = 0) {
  }

  getStatusText(): string {
    const defaultValue = '-';
    if (isNullOrUndefined(this.state)) {
      return 'Aguard Servidor';
    }
    if (this.state === DialerCallState.CREATED) {
      return 'Aguard Discagem';
    }
    if (this.state === DialerCallState.CREATION_ERROR) {
      return 'Erro de criação';
    }
    if (this.state === DialerCallState.DIALING) {
      return 'Discando';
    }
    if (this.state === DialerCallState.RINGING) {
      if (this.srcChannel.channelState === ChannelState.RINGING) {
        return 'Chamando Origem';
      }
      if (this.srcChannel.channelState === ChannelState.DIALING) {
        return 'Discando Origem';
      }
    }
    if (this.state === DialerCallState.UP) {
      if (this.srcChannel.channelState === ChannelState.ANSWERED) {
        if (this.dstChannel.channelState === ChannelState.UNKNOWN) {
          return 'Discando Destino';
        }
        if (this.dstChannel.channelState === ChannelState.DIALING) {
          return 'Discando Destino';
        }
        if (this.dstChannel.channelState === ChannelState.RINGING) {
          return 'Tocando Destino';
        }
        if (this.dstChannel.channelState === ChannelState.ANSWERED) {
          return 'Em Chamada';
        }
      }
    }
    if (this.state === DialerCallState.FINISHED) {
      if (this.srcChannel.channelState === ChannelState.BUSY) {
        return 'Tel Origem Ocupado';
      }
      if (this.srcChannel.channelState === ChannelState.NO_FREE_CHANNEL) {
        return 'Sem Canal Livre';
      }
      if (this.srcChannel.channelState === ChannelState.NON_EXISTENT) {
        return 'Origem Inexistente';
      }
      if (this.srcChannel.channelState === ChannelState.NOT_ANSWERED) {
        return 'Origem Não Atendeu';
      }
      if (this.srcChannel.channelState === ChannelState.REJECTED) {
        return 'Origem Rejeitou';
      }
      if (this.srcChannel.channelState === ChannelState.ANSWERED) {
        if (this.dstChannel.channelState === ChannelState.ANSWERED) {
          return 'Cham Finalizada';
        }
        if (this.dstChannel.channelState === ChannelState.BUSY) {
          return 'Tel Destino Ocupado';
        }
        if (this.dstChannel.channelState === ChannelState.NO_FREE_CHANNEL) {
          return 'Sem Canal Livre';
        }
        if (this.dstChannel.channelState === ChannelState.NON_EXISTENT) {
          return 'Destino Inexistente';
        }
        if (this.dstChannel.channelState === ChannelState.NOT_ANSWERED) {
          return 'Destino Não Atendeu';
        }
        if (this.dstChannel.channelState === ChannelState.REJECTED) {
          return 'Destino Rejeitou';
        }
      }
    }
    console.log(`Returning Status for ${DialerCallState[this.state]}|${ChannelState[this.srcChannel.channelState]}|` +
      `${ChannelState[this.dstChannel.channelState]}:${defaultValue}`);
    return defaultValue;
  }

  getStatusClass(): string {
    const defaultValue = 'success';
    if (isNullOrUndefined(this.state)) {
      return 'warning';
    }
    if (this.state === DialerCallState.CREATED || this.state === DialerCallState.DIALING ||
      this.state === DialerCallState.RINGING) {
      return 'warning';
    }
    if (this.state === DialerCallState.CREATION_ERROR) {
      return 'primary';
    }
    if (this.state === DialerCallState.UP) {
      if (this.srcChannel.channelState === ChannelState.ANSWERED &&
        (this.dstChannel.channelState === ChannelState.UNKNOWN ||
          this.dstChannel.channelState === ChannelState.DIALING)) {
        return 'info';
      } else {
        return 'danger';
      }
    }
    if (this.state === DialerCallState.FINISHED) {
      if (this.srcChannel.channelState === ChannelState.ANSWERED) {
        if (this.dstChannel.channelState === ChannelState.ANSWERED) {
          return 'success';
        } else {
          return 'primary';
        }
      } else {
        return 'primary';
      }
    }
    return defaultValue;
  }

  getStatusSymbolClass(): Array<string> {
    const defaultValue = ['flaticon-telephone-receiver-with-up-arrow'];
    if (isNullOrUndefined(this.state)) {
      return ['fa', 'fa-refresh'];
    }
    if (this.state === DialerCallState.CREATED) {
      return ['fa', 'fa-refresh'];
    }
    if (this.state === DialerCallState.CREATION_ERROR) {
      return ['fa', 'fa-exclamation-triangle'];
    }
    if (this.state === DialerCallState.DIALING) {
      return ['fa', 'fa-spinner', 'fa-pulse'];
    }
    if (this.state === DialerCallState.RINGING) {
      return ['flaticon-smartphone-ringing'];
    }
    if (this.state === DialerCallState.UP) {
      if (this.srcChannel.channelState === ChannelState.ANSWERED) {
        if (this.dstChannel.channelState === ChannelState.UNKNOWN) {
          return ['flaticon-hand-graving-smartphone'];
        } else if (this.dstChannel.channelState === ChannelState.DIALING) {
          return ['flaticon-hand-graving-smartphone'];
        } else if (this.dstChannel.channelState === ChannelState.RINGING) {
          return ['flaticon-smartphone-ringing'];
        }
      }
      return ['flaticon-boy-talking-by-phone'];
    }
    if (this.state === DialerCallState.FINISHED) {
      if (this.srcChannel.channelState === ChannelState.BUSY) {
        return ['flaticon-phone-auricular-with-high-volume'];
      } else if (this.srcChannel.channelState === ChannelState.ANSWERED) {
        if (this.dstChannel.channelState === ChannelState.ANSWERED) {
          return ['flaticon-telephone-receiver-with-up-arrow'];
        }
        if (this.dstChannel.channelState === ChannelState.BUSY || this.dstChannel.channelState === ChannelState.REJECTED) {
          return ['flaticon-phone-auricular-with-high-volume'];
        }
        if (this.dstChannel.channelState === ChannelState.NO_FREE_CHANNEL) {
          return ['flaticon-smartphone-with-sad-face-on-screen'];
        }
        if (this.dstChannel.channelState === ChannelState.NON_EXISTENT) {
          return ['flaticon-vintage-telephone'];
        }
        if (this.dstChannel.channelState === ChannelState.NOT_ANSWERED) {
          return ['flaticon-old-telephone-ringing'];
        }
      } else {
        return ['flaticon-smartphone-with-sad-face-on-screen'];
      }
    }
    return defaultValue;
  }

  isFinished(): boolean {
    if (this.state === DialerCallState.FINISHED || this.state === DialerCallState.CREATION_ERROR ||
      this.state === DialerCallState.BUSY) {
      return true;
    }
    return false;
  }
}

export enum DialerCallState {
  UNKNOWN,
  CREATED,
  CREATION_ERROR,
  DIALING, RINGING,
  UP,
  BUSY,
  FINISHED
}


export enum ChannelState {
  WAITING,
  DIALING,
  RINGING,
  ANSWERED,
  NOT_ANSWERED,
  REJECTED,
  UNAVAILABLE,
  NON_EXISTENT,
  BUSY,
  NO_FREE_CHANNEL,
  UNKNOWN
}

export class Channel {

  static fromJSON(json: any): Channel {
    if (isNullOrUndefined(json)) {
      return new Channel('', ChannelState.UNKNOWN);
    }
    const object: Channel = Object.create(Channel.prototype);
    Object.assign(object, json);
    object.channelState = ChannelState[<string>json.channelState];
    return object;
  }

  constructor(public uniqueId: string, public channelState: ChannelState) {
  }
}



// WEBPACK FOOTER //
// /Users/tabelini/development/jvm/tenacity/pysix/PysixCloud/src/app/dialer.service.ts